import { Button } from '@platform/ui-library';
import { ChangeEvent, FC, ReactNode, useRef, useState } from 'react';
import { downloadFile, readFile } from '@app/utilities';
import LoadingSpinner from '@app/widgets/components/LoadingSpinner';
import { InputAcceptType } from './types';

export type FileUploaderProps = {
  /** Types of files to accept. */
  accept?: InputAcceptType;
  /** Content displayed after to the file uploader. */
  childrenAfterUpload?: ReactNode;
  /** Content displayed before to the file uploader. */
  childrenBeforeUpload?: ReactNode;
  /**
   * Called after file finishes parsing into a string.
   *
   * @note If you need more fine control of parsing logic, then use @see onUpload instead.
   */
  onParse?: (text: string) => (Promise<void> | void);
  /**
   * Called when the user uploads a file.
   *
   * @note If you are just parsing the file into a string, then use @see onParse instead.
   */
  onUpload?: (file: File) => (Promise<void> | void);
  /**
   * Example file for the user to download.
   *
   * @note This property is required even though it is nullable to encourage adding a template.
   */
  template: {
    fileName: string;
    text: (() => string) | string;
  } | null;
};

export const FileUploader: FC<FileUploaderProps> = ({
  accept,
  childrenAfterUpload,
  childrenBeforeUpload,
  onParse,
  onUpload,
  template
}) => {
  const [hasFile, setHasFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const onChangeFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setHasFile(!!file);
    if (file) {
      setIsLoading(true);
      await onUpload?.(file);
      if (onParse) {
        await onParse(await readFile(file));
      }
      setIsLoading(false);
    }
  };

  const onClickUploadFile = async () => {
    inputFileRef.current?.click();
  };

  // Use default before message if `null` is not passed in.
  if (childrenBeforeUpload === undefined && !hasFile && template) {
    childrenBeforeUpload = (
      <p className="flex-center">
        Please upload a file that matches the provided template.
      </p>
    );
  }

  return (isLoading) ? (
    <LoadingSpinner message="Loading..." />
  ) : (
    <>
      {childrenBeforeUpload}
      <div className="flex-center flex-gap">
        {(!template) ? null : (
          <Button
            onClick={() => {
              downloadFile(template);
            }}
            theme="outline"
            type="button"
          >
            <i className="fe icon-download" />
            Download Template
          </Button>
        )}
        <Button
          type="button"
          theme="outlineHighlight"
          onClick={onClickUploadFile}
        >
          <i className="fe icon-upload" />
          {(hasFile) ? 'Change' : 'Upload'} File
        </Button>
      </div>
      <input
        accept={accept}
        className="hidden"
        onChange={onChangeFile}
        ref={inputFileRef}
        type="file"
      />
      {childrenAfterUpload}
    </>
  );
};
